import { z } from "zod";

/**
 * DecisionNodeSchema - Content Schema for Decision Investment Node
 *
 * Decision nodes record post-trade investment decisions.
 * Supports two scenarios:
 * 1. With Self-Contract (actionType = FOLLOW/OVERRIDE)
 * 2. Without Self-Contract (actionType = NONE)
 *
 * Field naming convention:
 * - ticker: Stock ticker symbol
 * - contractId: Reference to Self-Contract (optional)
 * - decision: BUY | SELL | HOLD
 * - actionType: FOLLOW | OVERRIDE | NONE
 * - price: Execution price
 * - quantity: Number of shares
 * - reason: Decision rationale
 * - executedAt: ISO 8601 timestamp of execution
 * - confidence: Confidence score (0-100, optional)
 * - riskLevel: LOW | MEDIUM | HIGH (optional)
 * - timeHorizon: Investment time horizon (optional)
 */
export const DecisionNodeSchema = z.object({
  // Protocol metadata
  type: z.literal("decision"),
  version: z.literal(1),
  key: z.string(),

  // Core business data (required)
  ticker: z.string().min(1).max(20),
  contractId: z.string().optional(),
  decision: z.enum(["BUY", "SELL", "HOLD"]),
  actionType: z.enum(["FOLLOW", "OVERRIDE", "NONE"]),
  price: z.number().nonnegative(),
  quantity: z.number().nonnegative(),
  reason: z.string().min(1),
  executedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/), // ISO 8601

  // Pre-analysis data (optional)
  confidence: z.number().min(0).max(100).optional().nullable(),
  riskLevel: z.enum(["LOW", "MEDIUM", "HIGH"]).optional().nullable(),
  timeHorizon: z.string().optional().nullable(),

  // Metadata
  createdAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/).optional(),
}).strict();

export type DecisionNode = z.infer<typeof DecisionNodeSchema>;
